import axios from "axios";
import { attachInterceptors } from "./interceptors";
import { getBaseUrl } from "./utils";
import { REQUEST_TIMEOUT_MS } from "./constants";

// Evaluated once at load; runtime env changes will not update this.
const BASE_URL = getBaseUrl();

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
});

axiosInstance.defaults.headers.common["Accept"] = "application/json";

// Middleware wrapping: request → axios → response/error run automatically; options passed via config.meta.
attachInterceptors(axiosInstance);

async function executeRequest(url, config = {}, startTimeFromCaller) {
  const { method = "GET", body = null, props = {} } = config;
  const startTime = startTimeFromCaller ?? performance.now();
  const preserved = config.meta || {};

  // Merge preserved meta so _retryCount, startTime, apiUrl survive on retry (avoids infinite refresh loop).
  const meta = {
    ...preserved,
    apiUrl: preserved.apiUrl ?? url,
    apiMethod: method,
    startTime: preserved.startTime ?? startTime,
    url,
    config,
    runTransport: (u, c) => executeRequest(u, c, preserved.startTime ?? startTime),
  };

  const axiosConfig = {
    method,
    url,
    headers: config.headers ?? {},
    data: body,
    ...(config.signal != null && { signal: config.signal }),
    validateStatus: () => true,
    props,
    meta,
    ...(config.tokenType != null && { tokenType: config.tokenType }),
    ...(config.authPrefix != null && { authPrefix: config.authPrefix }),
  };

  return axiosInstance(axiosConfig);
}

/**
 * HTTP client. Config may include:
 * - signal: AbortSignal (e.g. from AbortController) to cancel when component unmounts or navigation changes.
 * - json: boolean (default true) — true = normalized shape, false = raw axios response.
 * - tokenType: "access" | "guest" | "refresh" | "none" — which token to send (default "access").
 * - authPrefix: string — prefix for Authorization header, e.g. "Bearer" (default) or "carrum".
 */
const client = {
  get: (url, config = {}) => executeRequest(url, { ...config, method: "GET" }),

  post: (url, body = {}, config = {}) => executeRequest(url, { ...config, method: "POST", body }),

  put: (url, body = {}, config = {}) => executeRequest(url, { ...config, method: "PUT", body }),

  delete: (url, config = {}) => executeRequest(url, { ...config, method: "DELETE" }),
};

export default client;
